import { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Card, CardContent, CircularProgress, Alert,
  TextField, MenuItem, Chip, Button,
} from '@mui/material';
import { Analytics, Download, Print } from '@mui/icons-material';
import axios from 'axios';
import { LearnerAnalyticsBody, BRAND, exportLearnerXlsx } from '../components/LernAlytics';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ParentLernAlytics() {
  const [learners, setLearners] = useState([]);
  const [admNo, setAdmNo] = useState('');
  const [subject, setSubject] = useState('');
  const [term, setTerm] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/guardians/me/learners`, { headers: hdrs() })
      .then(r => {
        const kids = r.data?.data || [];
        setLearners(kids);
        if (kids.length) setAdmNo(kids[0].admNo);
      })
      .catch(() => {});
  }, []);

  const run = useCallback(async () => {
    if (!admNo) { setData(null); return; }
    setLoading(true); setError('');
    try {
      const params = {};
      if (subject) params.learningAreaCode = subject;
      if (term) params.term = term;
      const { data } = await axios.get(`${API}/api/academics/learner/${admNo}/lernalytics`, { headers: hdrs(), params });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally { setLoading(false); }
  }, [admNo, subject, term]);

  useEffect(() => { run(); }, [run]);

  const subjectsList = data?.filters?.subjects || [];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Analytics sx={{ color: BRAND, fontSize: 30 }} />
            <Box>
              <Typography variant="h5" sx={{ color: BRAND, fontWeight: 800 }}>LearnAlytics</Typography>
              <Typography variant="caption" color="text.secondary">Your child's performance over time — trends, projections & flags</Typography>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="Child" sx={{ minWidth: 200 }} value={admNo} onChange={e => setAdmNo(e.target.value)}>
              {learners.map(l => <MenuItem key={l.admNo} value={l.admNo}>{l.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Subject" sx={{ minWidth: 150 }} value={subject} onChange={e => setSubject(e.target.value)}>
              <MenuItem value=""><em>All subjects</em></MenuItem>
              {subjectsList.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Term" sx={{ width: 110 }} value={term} onChange={e => setTerm(e.target.value)}>
              <MenuItem value=""><em>All</em></MenuItem>
              {[1, 2, 3].map(t => <MenuItem key={t} value={String(t)}>Term {t}</MenuItem>)}
            </TextField>
            <Box flexGrow={1} />
            <Button size="small" variant="outlined" startIcon={<Download />} disabled={!data} onClick={() => exportLearnerXlsx(data)}>Excel</Button>
            <Button size="small" variant="outlined" startIcon={<Print />} disabled={!data} onClick={() => window.print()}>Print</Button>
            {loading && <CircularProgress size={20} />}
          </Box>
        </CardContent>
      </Card>

      {!admNo && <Alert severity="info">No child linked to your account yet.</Alert>}

      {data && admNo && (
        <>
          <Box mb={2} px={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" fontWeight={800}>{data.learner?.name}</Typography>
            {data.learner?.stage && <Chip size="small" label={data.learner.stage} />}
            <Typography variant="body2" color="text.secondary">{data.periods?.length || 0} period(s) analysed</Typography>
          </Box>
          <LearnerAnalyticsBody data={data} />
          <Alert severity="info" icon={false} sx={{ mt: 2, fontSize: 13 }}>
            The projection is a straight-line estimate from past terms — a prompt for support, not a prediction. The 40% line marks the expectation threshold.
          </Alert>
        </>
      )}
    </Container>
  );
}
